///
/// EarthBrowser
///
/// Created by Matt Giger on 10/26/2026
/// Copyright (c) 2026 Geodata Labs
///
/// The Slab2 subduction interface as a surface through the cutout: a grid
/// over the footprint sampled from the slab model, clipped to the box
/// depth, with depth contours in the shader. Built in the block frame with
/// the depth axis stretched like everything else.
///

import { NodeBase } from "@stovokor/engine/NodeBase"
import { Renderer } from "@stovokor/engine/Renderer"
import { type RenderState } from "@stovokor/engine/RenderState"
import { type cube } from "@stovokor/math/cube"
import { type matrix } from "@stovokor/math/matrix"
import { type sphere } from "@stovokor/math/sphere"
import { type Geometry } from "@stovokor/shader/Geometry"
import { DrawMode } from "@stovokor/shader/GLConstants"
import { IndexBuffer } from "@stovokor/shader/IndexBuffer"
import { type Material } from "@stovokor/shader/Material"
import { ShaderSourceDefinition } from "@stovokor/shader/ShaderSourceCode"
import { UniformDynamic } from "@stovokor/shader/Uniform"
import { VertexAttribute } from "@stovokor/shader/VertexAttribute"
import { VertexBuffer } from "@stovokor/shader/VertexBuffer"
import { type StovokorContext } from "@stovokor/StovokorContext"
import { fromBlockXZ, localToLatLon, type CutoutLayout } from "./cutoutLayout"
import { type Slab2Surface } from "./Slab2Query"

const cGridAlong = 96

export class SlabSurfaceNode extends NodeBase {
	private readonly slabRenderer: SlabSurfaceRenderer
	/** Vertices that fell inside the box; 0 means nothing to show */
	public readonly vertexCount: number

	constructor(context: StovokorContext, layout: CutoutLayout, slab: Slab2Surface, lat0: number, lon0: number) {
		super()
		const geometry = new SlabSurfaceGeometry(context, layout, slab, lat0, lon0)
		this.vertexCount = geometry.vertexCount
		this.slabRenderer = new SlabSurfaceRenderer(context, geometry)
		this.slabRenderer.tick.value = layout.tickKm
		this.renderer = this.slabRenderer
		this.renderOrder = 1
	}

	public set opacity(value: number) {
		this.slabRenderer.opacity.value = value
	}
}

class SlabSurfaceRenderer extends Renderer {
	public modelViewProjection = new UniformDynamic<matrix>()
	public tick = new UniformDynamic<number>(10)
	public opacity = new UniformDynamic<number>(0)

	constructor(context: StovokorContext, geometry: Geometry) {
		const transparentMaterial: Material = { transparency: true, available: true }
		super(context, new ShaderSourceDefinition("cutout_slab"), geometry, [transparentMaterial])
		this.drawState.cullFace = false
	}

	protected stateUpdate(state: RenderState): void {
		this.modelViewProjection.value = state.modelViewProjection
	}
}

class SlabSurfaceGeometry implements Geometry {
	public position: VertexAttribute
	public normal: VertexAttribute
	public depth: VertexAttribute
	public indexBuffer: IndexBuffer
	public vertexBuffer: VertexBuffer
	public mode: DrawMode = DrawMode.Triangles
	public bounds: cube
	public boundingSphere: sphere
	public available: boolean = true
	public readonly vertexCount: number

	constructor(context: StovokorContext, layout: CutoutLayout, slab: Slab2Surface, lat0: number, lon0: number) {
		this.position = new VertexAttribute(context, 3)
		this.normal = new VertexAttribute(context, 3)
		this.depth = new VertexAttribute(context, 1)
		this.indexBuffer = new IndexBuffer(context)
		this.vertexBuffer = new VertexBuffer(context, [this.position, this.normal, this.depth])

		const nx = cGridAlong
		const nz = Math.max(24, Math.round(cGridAlong * layout.widthKm / layout.lengthKm))
		const dx = layout.lengthKm / (nx - 1)
		const dz = layout.widthKm / (nz - 1)
		const halfX = layout.lengthKm / 2
		const halfZ = layout.widthKm / 2
		// Let the surface run a little past the floor so it exits through it
		const maxDepth = layout.depthFloorKm * 1.02

		// Sample true depths; NaN where the slab is absent or below the box
		const depths = new Float32Array(nx * nz).fill(NaN)
		for(let j = 0; j < nz; j++) {
			const z = -halfZ + j * dz
			for(let i = 0; i < nx; i++) {
				const x = -halfX + i * dx
				const local = fromBlockXZ(x, z, layout.strike)
				const geo = localToLatLon(local.east + layout.centerEast, local.north + layout.centerNorth, lat0, lon0)
				const d = slab.sampleDepth(geo.lat, geo.lon)
				if(d !== undefined && d <= maxDepth && d >= 0) depths[j * nx + i] = d
			}
		}

		// Vertices (index map so cells with a missing corner are skipped)
		const index = new Int32Array(nx * nz).fill(-1)
		const scale = layout.depthScale
		const at = (i: number, j: number): number => depths[Math.min(nz - 1, Math.max(0, j)) * nx + Math.min(nx - 1, Math.max(0, i))]
		let count = 0
		for(let j = 0; j < nz; j++) {
			for(let i = 0; i < nx; i++) {
				const d = at(i, j)
				if(Number.isNaN(d)) continue
				// One-sided differences where a neighbour is missing
				const dxp = Number.isNaN(at(i + 1, j)) ? d : at(i + 1, j)
				const dxm = Number.isNaN(at(i - 1, j)) ? d : at(i - 1, j)
				const dzp = Number.isNaN(at(i, j + 1)) ? d : at(i, j + 1)
				const dzm = Number.isNaN(at(i, j - 1)) ? d : at(i, j - 1)
				const dydx = -(dxp - dxm) * scale / (2 * dx)
				const dydz = -(dzp - dzm) * scale / (2 * dz)
				const len = Math.hypot(dydx, 1, dydz)
				const x = -halfX + i * dx
				const z = -halfZ + j * dz
				this.position.addPos(x, -d * scale, z)
				this.normal.add(-dydx / len, 1 / len, -dydz / len)
				this.depth.add(d)
				index[j * nx + i] = count++
			}
		}
		this.vertexCount = count
		for(let j = 0; j < nz - 1; j++) {
			for(let i = 0; i < nx - 1; i++) {
				const a = index[j * nx + i], b = index[j * nx + i + 1]
				const c = index[(j + 1) * nx + i + 1], d = index[(j + 1) * nx + i]
				if(a < 0 || b < 0 || c < 0 || d < 0) continue
				this.indexBuffer.quad(a, d, c, b)
			}
		}

		if(count === 0) {
			// Keep the buffers valid; nothing will be drawn
			this.position.addPos(0, 0, 0)
			this.normal.add(0, 1, 0)
			this.depth.add(0)
		}
		this.bounds = this.position.bounds
		this.boundingSphere = this.bounds.sphere
	}

	public destroy(): void {
		this.vertexBuffer.destroy()
		this.indexBuffer.destroy()
	}
}
